import { API_BASE_URL } from "../configuration/config";
import { StatusCodes } from "http-status-codes";
import * as cache from "memory-cache";
import { BaseService } from "./base-service";
import { getString } from "common/ui_string_labels/ui-strings-utils";
import { AmazonianInformation, IndividualPermissionProps } from "interfaces";

const getStringPath: string = "services.user-permissions";

const PERMISSIONS_CACHE_KEY = "PERMISSIONS";
const PERMISSIONS_CACHE_EXPIRATION_IN_MILLISECONDS = 5 * 60 * 1000; // 5 minutes

export const USER_PERMISSIONS_ENDPOINT: string = "/user-permissions";
export const USER_INFORMATION_ENDPOINT = "user-info";

/**
 * Implements user Validation, Authorization and Authentication.
 */
export class UserPermissions extends BaseService {
    /**
     * Singleton instance with the current user permissions.
     */
    public static instance = new UserPermissions(API_BASE_URL);

    private username: string | null = null;
    private refreshPromise: Promise<void> | null = null;
    private _permissions: IndividualPermissionProps[] | null = null;

    /**
     * Retrieves the username of the currently authenticated user.
     */
    public get getUsername(): string {
        return this.username ? this.username : getString(`${getStringPath}.usernameUnavailable`);
    }

    /**
     * Retrieves the list of permissions authorized to the current user.
     */
    public get permissions(): IndividualPermissionProps[] {
        const permissions = cache.get(PERMISSIONS_CACHE_KEY) as IndividualPermissionProps[];
        if (!permissions) {
            throw new Error(getString(`${getStringPath}.userNotAuthorized`));
        }

        return permissions;
    }

    /**
     * Identifies whether or not the current used has been through the authorization process, irrespective of the result.
     * @returns True if the user has been through the authorization process.
     */
    public get hasAuthorized(): boolean {
        return cache.get(PERMISSIONS_CACHE_KEY) != null;
    }

    /**
     * Identifies whether or not the current user has a given permission.
     * @param permission The permission to check.
     * @param preferReturn Optional boolean parameter to specify return while permissions are refreshing. Defaults to false.
     * @returns True if the user has the specified permission, or preferReturn value if permissions are expired.
     */
    public hasPermission(permission: IndividualPermissionProps, preferReturn: boolean = false): boolean {
        const cachedPermissions = cache.get(PERMISSIONS_CACHE_KEY) as IndividualPermissionProps[];

        // Use cached permissions if available
        if (cachedPermissions) {
            this._permissions = cachedPermissions;
            return cachedPermissions.includes(permission);
        }

        // If cache is expired but there are previous permissions, use them while refreshing
        if (this._permissions) {
            this.refreshPermissionsSilent().catch(() => {});
            return this._permissions.includes(permission);
        }

        // No permissions available, trigger refresh and return preferReturn
        this.refreshPermissionsSilent().catch(() => {});
        return preferReturn;
    }

    /**
     * Executes authorization (and indirectly authentication) for the current user
     *
     * @return {Promise<void>} - Promise that resolves when authorization is complete
     */
    public async authorize() {
        if (!cache.get(PERMISSIONS_CACHE_KEY)) {
            const permissions = await this.callPermissionsService();
            this._permissions = permissions;
            cache.put(
                PERMISSIONS_CACHE_KEY,
                permissions,
                PERMISSIONS_CACHE_EXPIRATION_IN_MILLISECONDS,
            );
        }
    }

    /**
     * Refreshes user permissions asynchronously in the background.
     * Makes a request to fetch latest permissions and updates the cache.
     * 
     * @returns {Promise<void>} Promise that resolves when permissions refresh is complete
     * @throws {Error} If the permissions refresh request fails
     */
    public async refreshPermissionsSilent(): Promise<void> {
        // Return existing promise if one is in progress
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        // Create new promise and store it
        this.refreshPromise = (async () => {
            try {
                const response = await this.fetch(USER_PERMISSIONS_ENDPOINT);

                if (response.status === StatusCodes.OK) {
                    const jsonResponse = await response.json();
                    if (!jsonResponse.failureReason) {
                        this.username = jsonResponse.username;
                        const newPermissions = jsonResponse.allowedFeatures as IndividualPermissionProps[];

                        this._permissions = newPermissions;
                        cache.put(
                            PERMISSIONS_CACHE_KEY,
                            newPermissions,
                            PERMISSIONS_CACHE_EXPIRATION_IN_MILLISECONDS,
                        );
                    }
                }
            } catch (error) {
                throw error;
            } finally {
                this.refreshPromise = null;
            }
        })();

        return this.refreshPromise;
    }

    /**
     * Calls the permissions service to retrieve the list of permissions for the current user
     *
     * @return {Promise<IndividualPermissionProps[]>} - Promise that resolves to an array of permission strings
     */
    private async callPermissionsService(): Promise<IndividualPermissionProps[]> {
        const response = await this.fetch(USER_PERMISSIONS_ENDPOINT);
        if (response.status !== StatusCodes.OK) {
            throw new Error(getString(`${getStringPath}.errorAuthorizingUser`));
        }

        const jsonResponse = await response.json();
        if (jsonResponse.failureReason) {
            throw new Error(jsonResponse.failureReason);
        }

        this.username = jsonResponse.username;

        return jsonResponse.allowedFeatures as IndividualPermissionProps[];
    }

    /**
     * Retrieves Amazonian information for a given login
     *
     * @param {string} login - The login/username to look up
     * @return {Promise<AmazonianInformation>} - Promise that resolves to the Amazonian's information
     */
    private async getAmazonian(login: string): Promise<AmazonianInformation | null> {
        const response = await this.fetch(`${USER_INFORMATION_ENDPOINT}/${encodeURIComponent(login)}`);

        if (response.status === StatusCodes.NOT_FOUND) {
            return null;
        }

        if (response.status !== StatusCodes.OK) {
            throw new Error(getString(`${getStringPath}.errorRetrievingUserInfo`));
        }

        return await response.json();
    }

    /**
     * Validates if a given login belongs to a valid Amazonian
     *
     * @param {string} login - The login/username to validate
     * @return {Promise<boolean>} - Promise that resolves to true if the login is valid, false otherwise
     */
    public async isValidAmazonianLogin(login: string): Promise<boolean> {
        try {
            const user = await this.getAmazonian(login);
            return user !== null;
        } catch (error) {
            return false;
        }
    }
}
